import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchemaSummary} from 'constants/RegistryConstants';
import {
  ProcedureParameterConfirmationMethod,
  ProcedureType,
  TagSchemaItemType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isTypedValueEmpty, parseTextAsFloat} from 'notebook/tables/typed-values/DataTypes';
import {
  DatetimeSchemaFieldTypedValue,
  FloatSchemaFieldTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import {MAX_PARAMETER_NAME_LENGTH} from 'procedures/design/ProcedureDesignConstants';
import {hasArchivedModelSchema} from 'procedures/design/utils/equipmentSchemaHelpers';
import {
  getEntitySchemaMismatchWithParameterSettingErrorMessage,
  ProcedureValidationContext,
} from 'procedures/design/validation/procedureValidationUtils';
import {
  EquipmentTypedValue,
  getMethodComponentCid,
  getMethodComponentName,
  MaterialInputTypedValue,
  Method,
  MethodComponent,
  ProcedureDataParameterSetting,
  ProcedureEquipmentSetting,
  ProcedureMaterialInputSetting,
  ProcedureMaterialOutputSetting,
} from 'procedures/ProcedureDomainTypes';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {
  EquipmentType,
  isDropdownDataParameterType,
  MaterialType,
} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {isEquipmentValidWithModel} from 'procedures/shared-components/validation/isEquipmentValidWithModel';
import {isDataParameterSettingEmpty} from 'procedures/tables/BaseProcedureDataParametersConfigTableAdapter';
import ProcedureEquipmentTableAdapter from 'procedures/tables/ProcedureEquipmentTableAdapter';
import ProcedureMaterialInputsTableAdapter from 'procedures/tables/ProcedureMaterialInputsTableAdapter';
import ProcedureMaterialOutputsTableAdapter from 'procedures/tables/ProcedureMaterialOutputsTableAdapter';
import {doesSchemaImplementEquipmentInterface} from 'procedures/utils/useAllTagSchemas';
import {
  FieldType,
  getRequiredLinkTagSchema,
  isRequiredLinkCustomSchemaInterface,
} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';
import nameToSqlIdentifier, {FieldNamingException} from 'warehouse/nameToSqlIdentifier';

import {TagSchemaSummaryGraphQL} from '../../../../../graphql-fragments/schemas/tag-schemas/__generated/TagSchemaSummaryGraphQL';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObject,
  ProcedureValidationObjectType,
} from '../ProcedureValidationTypes';
import {
  extractMethodComponentsNames,
  extractDuplicateNames,
  createErrorsForMethodComponentsWithMatchingName,
} from '../procedureValidationUtils';
import {isNameEmpty} from '../validateProcedure';

function doesMethodComponentNameExceedMaxLength(name: string | null | undefined): boolean {
  return name != null && name.trim().length > MAX_PARAMETER_NAME_LENGTH;
}

function formatErrorMessage(procedureObjectType: string, errorMessage: string): string {
  return `${procedureObjectType} ${errorMessage}`;
}

function validateNonEmptyMethodSettingNames(
  methodComponents: Array<MethodComponent>,
  objectType: ProcedureValidationObjectType,
  parentObjects: Array<ProcedureValidationObject>
): Array<ProcedureValidationError> {
  return methodComponents
    .filter((p) => isNameEmpty(getMethodComponentName(p)))
    .map((p) => ({
      errorMessage: 'Name is required',
      type: ProcedureValidationErrorType.INVALID_NAME,
      primaryObject: {id: getMethodComponentCid(p), objectType},
      parentObjects,
    }));
}

function validateParameterSettingsHaveCorrectTypedValues<
  TSetting extends ProcedureMaterialInputSetting | ProcedureEquipmentSetting,
  TType extends TSetting extends ProcedureMaterialInputSetting ? MaterialType : EquipmentType,
  TTypedValue extends TSetting extends ProcedureMaterialInputSetting
    ? MaterialInputTypedValue
    : EquipmentTypedValue,
>(
  parameterSettings: Array<TSetting>,
  parentObjects: Array<ProcedureValidationObject>,
  getDataType: (parameterSetting: TSetting) => TType,
  getPlannedValue: (parameterSetting: TSetting) => TTypedValue | null,
  objectType: ProcedureValidationObjectType,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  assert(
    objectType === ProcedureValidationObjectType.MATERIAL_INPUT ||
      objectType === ProcedureValidationObjectType.EQUIPMENT,
    'Only material input and equipment parameters are supported'
  );
  return parameterSettings
    .map((ps) => {
      const dataType = getDataType(ps);
      const isBioentityLinkType = dataType.type === FieldType.BIOENTITY_LINK;
      if (!isBioentityLinkType) {
        return {ps, errorMessage: null};
      }

      const defaultEntity = getPlannedValue(ps);
      if (!defaultEntity) {
        return {ps, errorMessage: null};
      }

      if (
        // Check that the planned value type matches the data type
        defaultEntity.type !== FieldType.BIOENTITY_LINK ||
        // Check that an entity is selected if user entered text into table cell
        (defaultEntity.value !== '' && defaultEntity.targetFiles.length === 0)
      ) {
        return {ps, errorMessage: 'A linked entity is required'};
      }

      const entitySchemaMismatchErrorMessage = getEntitySchemaMismatchWithParameterSettingErrorMessage(
        defaultEntity,
        ps,
        ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE,
        context
      );

      if (entitySchemaMismatchErrorMessage) {
        return {ps, errorMessage: entitySchemaMismatchErrorMessage};
      }
      return {ps, errorMessage: null};
    })
    .filter(({errorMessage}) => errorMessage != null)
    .map(({ps, errorMessage}) => ({
      errorMessage: errorMessage as string,
      type: ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE,
      primaryObject: {id: getMethodComponentCid(ps), objectType},
      parentObjects,
    }));
}

function validateDefaultMaterialInputSettingsHaveCorrectTypedValues(
  materialInputSettings: Array<ProcedureMaterialInputSetting>,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  return validateParameterSettingsHaveCorrectTypedValues(
    materialInputSettings,
    parentObjects,
    (m: ProcedureMaterialInputSetting) => m.materialInputVersion.dataType,
    (m: ProcedureMaterialInputSetting) => {
      const plannedValue = m.values.length > 0 ? m.values[0] : null;
      if (plannedValue) {
        return plannedValue.material.value != null ? plannedValue.material : null;
      }
      return null;
    },
    ProcedureValidationObjectType.MATERIAL_INPUT,
    context
  );
}

function validateDefaultEquipmentSettingsWithModels(
  equipmentSettings: Array<ProcedureEquipmentSetting>,
  parentObjects: Array<ProcedureValidationObject>,
  getPlannedValue: (e: ProcedureEquipmentSetting) => EquipmentTypedValue | null,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  return equipmentSettings
    .map((eq) => {
      const plannedValue = getPlannedValue(eq);
      const models = eq.equipmentVersion.models;

      if (plannedValue && context.bioentitiesForProcedures) {
        if (!isEquipmentValidWithModel(plannedValue, models, context.bioentitiesForProcedures)) {
          return {eq, errorMessage: `Equipment's model does not match the model you have selected`};
        }
      }
      return {eq, errorMessage: null};
    })
    .filter(({errorMessage}) => errorMessage != null)
    .map(({eq, errorMessage}) => ({
      errorMessage: errorMessage as string,
      type: ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE,
      primaryObject: {id: getMethodComponentCid(eq), objectType: ProcedureValidationObjectType.EQUIPMENT},
      parentObjects,
    }));
}

function validateDefaultEquipmentSettingsHaveCorrectTypedValues(
  equipmentSettings: Array<ProcedureEquipmentSetting>,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  const getPlannedValue = (e: ProcedureEquipmentSetting): EquipmentTypedValue | null => {
    const plannedValue = e.values.length > 0 ? e.values[0] : null;
    if (plannedValue) {
      return plannedValue.equipment.value != null ? plannedValue.equipment : null;
    }
    return null;
  };
  return [
    ...validateParameterSettingsHaveCorrectTypedValues(
      equipmentSettings,
      parentObjects,
      (e: ProcedureEquipmentSetting) => e.equipmentVersion.dataType,
      getPlannedValue,
      ProcedureValidationObjectType.EQUIPMENT,
      context
    ),
    ...validateDefaultEquipmentSettingsWithModels(equipmentSettings, parentObjects, getPlannedValue, context),
  ];
}

function validateEquipmentSettingsModelsHaveCorrectTypedValues(
  equipmentSettings: Array<ProcedureEquipmentSetting>,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  return equipmentSettings
    .map((eq) => {
      const isBioentityLinkType = eq.equipmentVersion.dataType.type === FieldType.BIOENTITY_LINK;

      if (!isBioentityLinkType) {
        return {eq, errorMessage: null};
      }
      const models = eq.equipmentVersion.models;
      const modelType = models.type;
      if (
        // Check that the models value type matches the equipment setting type
        modelType !== FieldType.BIOENTITY_LINK ||
        // Check that an entity is selected if user entered text into table cell
        (models.value && models.targetFiles.length === 0)
      ) {
        return {eq, errorMessage: 'A linked entity is required'};
      }

      const entitySchemaMismatchErrorMessage = getEntitySchemaMismatchWithParameterSettingErrorMessage(
        models,
        eq,
        ProcedureValidationErrorType.MISMATCH_MODELS_TYPE,
        context
      );

      if (entitySchemaMismatchErrorMessage) {
        return {eq, errorMessage: entitySchemaMismatchErrorMessage};
      }

      return {eq, errorMessage: null};
    })
    .filter(({errorMessage}) => errorMessage != null)
    .map(({eq, errorMessage}) => ({
      errorMessage: errorMessage as string,
      type: ProcedureValidationErrorType.MISMATCH_MODELS_TYPE,
      primaryObject: {id: getMethodComponentCid(eq), objectType: ProcedureValidationObjectType.EQUIPMENT},
      parentObjects,
    }));
}

function validateEquipmentModelEntitiesNotArchived(
  equipmentSettings: Array<ProcedureEquipmentSetting>,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  const {bioentitiesForProcedures, tagSchemas} = context;
  if (bioentitiesForProcedures == null || tagSchemas == null) {
    return [];
  }

  const bioentityById = new Map(bioentitiesForProcedures.map((e) => [e.id, e]));
  const tagSchemaById = new Map(tagSchemas.map((ts) => [ts.id, ts]));

  return equipmentSettings.flatMap((eq) => {
    const models = eq.equipmentVersion.models;
    if (models.type !== FieldType.BIOENTITY_LINK || models.targetFiles.length === 0) {
      return [];
    }

    return models.targetFiles.flatMap((targetFile) => {
      const entity = bioentityById.get(targetFile.id ?? '');
      if (entity?.tagSchema == null) {
        return [];
      }
      const entitySchema = tagSchemaById.get(entity.tagSchema.id);
      if (entitySchema == null || entitySchema.archiveRecord == null) {
        return [];
      }
      return [
        {
          errorMessage: `${targetFile.name}'s schema is archived`,
          type: ProcedureValidationErrorType.ARCHIVED_MODEL_ENTITY_SCHEMA,
          primaryObject: {id: getMethodComponentCid(eq), objectType: ProcedureValidationObjectType.EQUIPMENT},
          parentObjects,
        },
      ];
    });
  });
}

function validateMethodSettingNamesBelowMaxLength(
  methodComponents: Array<MethodComponent>,
  objectType: ProcedureValidationObjectType,
  parameterType: string,
  parentObjects: Array<ProcedureValidationObject>
): Array<ProcedureValidationError> {
  const errorMessage = formatErrorMessage(parameterType, 'name is too long');

  return methodComponents
    .filter((p) => doesMethodComponentNameExceedMaxLength(getMethodComponentName(p)))
    .map((p) => ({
      errorMessage,
      type: ProcedureValidationErrorType.INVALID_NAME,
      primaryObject: {id: getMethodComponentCid(p), objectType},
      parentObjects,
    }));
}

/**
 * Returns a user-friendly error message if the given name cannot become a valid SQL identifier,
 * or null if the name is valid or should be skipped (empty or exceeds max length).
 */
export function getParameterNameSqlIdentifierErrorMessage(
  name: string,
  parameterType: string = 'Parameter'
): string | null {
  if (isNameEmpty(name) || doesMethodComponentNameExceedMaxLength(name)) {
    return null;
  }
  try {
    nameToSqlIdentifier(name, true);
    return null;
  } catch (error) {
    if (error instanceof FieldNamingException) {
      return `${parameterType} name must contain at least one alphanumeric character`;
    }
    throw error;
  }
}

/** @package */
export function validateParameterNamesCanBecomeValidSqlIdentifiers(
  methodComponents: Array<MethodComponent>,
  objectType: ProcedureValidationObjectType,
  parameterType: string,
  parentObjects: Array<ProcedureValidationObject>
): Array<ProcedureValidationError> {
  return methodComponents
    .map((component) => {
      const name = getMethodComponentName(component);
      const errorMessage = getParameterNameSqlIdentifierErrorMessage(name, parameterType);
      if (errorMessage == null) {
        return null;
      }
      return {
        errorMessage,
        type: ProcedureValidationErrorType.INVALID_NAME,
        primaryObject: {id: getMethodComponentCid(component), objectType},
        parentObjects,
      };
    })
    .filter((error): error is ProcedureValidationError => error != null);
}

function extractComparableMinMaxValue(
  typedValue: FloatSchemaFieldTypedValue | DatetimeSchemaFieldTypedValue | null
): number | null {
  if (typedValue == null || isTypedValueEmpty(typedValue)) {
    return null;
  }
  if (typedValue.type === FieldType.DATETIME) {
    const date = new Date(typedValue.value!);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  }
  return parseTextAsFloat(typedValue.value!);
}

export function validateDataParameterMinMaxRanges(
  dataParameterSettings: Array<ProcedureDataParameterSetting>,
  parentObjects: Array<ProcedureValidationObject>
): Array<ProcedureValidationError> {
  const parameterData = dataParameterSettings.map((p) => ({
    id: getMethodComponentCid(p),
    minimum: extractComparableMinMaxValue(p.dataParameterVersion.minimum),
    maximum: extractComparableMinMaxValue(p.dataParameterVersion.maximum),
    setpoints: p.values.map((v) =>
      extractComparableMinMaxValue(v.value as FloatSchemaFieldTypedValue | DatetimeSchemaFieldTypedValue)
    ),
  }));

  const invalidRanges = parameterData
    .filter((data) => data.minimum != null && data.maximum != null && data.minimum > data.maximum)
    .map((data) => ({
      errorMessage: 'When both min and max are specified, max must be greater than or equal to min',
      type: ProcedureValidationErrorType.INVALID_PARAMETER_RANGE,
      primaryObject: {id: data.id, objectType: ProcedureValidationObjectType.DATA_PARAMETER},
      parentObjects,
    }));

  const invalidDefaults = parameterData
    .filter((data) => {
      let hasError = false;
      if (data.minimum != null) {
        const minimum = data.minimum;
        hasError = hasError || data.setpoints.some((v) => v != null && v < minimum);
      }
      if (data.maximum != null) {
        const maximum = data.maximum;
        hasError = hasError || data.setpoints.some((v) => v != null && v > maximum);
      }

      return hasError;
    })
    .map((data) => ({
      errorMessage: 'Setpoint must be within specified min/max range',
      type: ProcedureValidationErrorType.SETPOINT_NOT_IN_RANGE,
      primaryObject: {id: data.id, objectType: ProcedureValidationObjectType.DATA_PARAMETER},
      parentObjects,
    }));

  return invalidRanges.concat(invalidDefaults);
}

function validateDataParameterLibraryLinkEnforcement(
  dataParameterSettings: Array<ProcedureDataParameterSetting>,
  parentObjects: Array<ProcedureValidationObject>,
  procedureType: ProcedureType
): Array<ProcedureValidationError> {
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
    plural: true,
  });
  if (BenchlingConfig.PROCEDURES_ENABLE_PARAMETER_LIBRARY_USE_AND_ENFORCEMENT) {
    return dataParameterSettings
      .filter((p) => p.dataParameterVersion.libraryParameterDefinition == null)
      .map((p) => ({
        errorMessage: `All parameters used in ${procedureString} on this tenant must be from the parameter library`,
        type: ProcedureValidationErrorType.PARAMETER_LIBRARY_LINK_REQUIRED,
        primaryObject: {
          id: getMethodComponentCid(p),
          objectType: ProcedureValidationObjectType.DATA_PARAMETER,
        },
        parentObjects,
      }));
  }
  return dataParameterSettings
    .filter((p) => p.dataParameterVersion.libraryParameterDefinition != null)
    .map((p) => ({
      errorMessage: 'Link to library parameter is not allowed on this tenant',
      type: ProcedureValidationErrorType.PARAMETER_LIBRARY_LINK_DISALLOWED,
      primaryObject: {id: getMethodComponentCid(p), objectType: ProcedureValidationObjectType.DATA_PARAMETER},
      parentObjects,
    }));
}

function validateDataParameterLibraryDefinitionsNotArchived(
  dataParameterSettings: Array<ProcedureDataParameterSetting>,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  const {archivedLibraryParameterDefinitionIds} = context;
  if (archivedLibraryParameterDefinitionIds == null) {
    return [];
  }

  return dataParameterSettings
    .filter((p) => {
      const libDef = p.dataParameterVersion.libraryParameterDefinition;
      return libDef != null && archivedLibraryParameterDefinitionIds.has(libDef.id);
    })
    .map((p) => ({
      errorMessage: `Library parameter definition '${p.dataParameterVersion.libraryParameterDefinition!.name}' is archived`,
      type: ProcedureValidationErrorType.ARCHIVED_LIBRARY_PARAMETER_DEFINITION,
      primaryObject: {
        id: getMethodComponentCid(p),
        objectType: ProcedureValidationObjectType.DATA_PARAMETER,
      },
      parentObjects,
    }));
}

/**
 * Flags dropdown (selector) data parameters whose schema field selector is missing or malformed.
 *
 * We deliberately preserve such misconfigured dropdowns when loading a procedure (see
 * `parseDataParameterTypeFromNetwork`) rather than silently coercing them to a decimal, so this
 * validation is what surfaces the problem to the user instead of the configuration being clobbered.
 */
function validateDropdownDataParameterSelectors(
  dataParameterSettings: Array<ProcedureDataParameterSetting>,
  parentObjects: Array<ProcedureValidationObject>
): Array<ProcedureValidationError> {
  if (!BenchlingConfig.PROCEDURES_ENABLE_DROPDOWN_DATA_PARAMETER) {
    return [];
  }
  return dataParameterSettings
    .filter((p) => {
      const dataType = p.dataParameterVersion.dataType;
      if (!isDropdownDataParameterType(dataType)) {
        return false;
      }
      const {schemaFieldSelectorId} = dataType;
      return (
        schemaFieldSelectorId === '' ||
        APIUtil.apiIdToMaybeType(schemaFieldSelectorId) !== ModelTypeConstants.SCHEMA_FIELD_SELECTOR
      );
    })
    .map((p) => ({
      errorMessage: 'Dropdown parameter must have a valid dropdown selected',
      type: ProcedureValidationErrorType.INVALID_DROPDOWN_PARAMETER_SELECTOR,
      primaryObject: {
        id: getMethodComponentCid(p),
        objectType: ProcedureValidationObjectType.DATA_PARAMETER,
      },
      parentObjects,
    }));
}

function validateMaterialInputAmounts(
  materialInputSettings: Array<ProcedureMaterialInputSetting>,
  parentObjects: Array<ProcedureValidationObject>
): Array<ProcedureValidationError> {
  return materialInputSettings
    .filter((m) => m.values.some((v) => v.amount?.floatValue != null && v.amount.floatValue < 0))
    .map((m) => ({
      errorMessage: 'Amount value cannot be less than 0',
      type: ProcedureValidationErrorType.NEGATIVE_AMOUNT,
      primaryObject: {id: getMethodComponentCid(m), objectType: ProcedureValidationObjectType.MATERIAL_INPUT},
      parentObjects,
    }));
}

function validateMaterialInputConfirmationMethodHasCompatibleDataType(
  setting: ProcedureMaterialInputSetting,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  const version = setting.materialInputVersion;
  const errors: Array<ProcedureValidationError> = [];
  if (
    version.confirmationMethod === ProcedureParameterConfirmationMethod.PLATE_MAP &&
    version.dataType.type !== FieldType.BIOENTITY_LINK
  ) {
    errors.push({
      // Because plate maps perform a transfer. You can't transfer text.
      errorMessage: 'Material inputs confirmed via plate map must have entity data type',
      type: ProcedureValidationErrorType.NON_ENTITY_MATERIAL_INPUT_CONFIRMED_VIA_PLATE_MAP,
      primaryObject: {
        id: getMethodComponentCid(setting),
        objectType: ProcedureValidationObjectType.MATERIAL_INPUT,
      },
      parentObjects,
    });
  }
  const requiredEntitySchemaId = getRequiredLinkTagSchema({requiredLink: version.dataType.requiredLink})?.id;
  const requiredEntitySchema = context.tagSchemas?.find(({id}) => id === requiredEntitySchemaId);
  if (
    version.confirmationMethod === ProcedureParameterConfirmationMethod.PLATE_MAP &&
    requiredEntitySchema != null &&
    !requiredEntitySchema.isContainable
  ) {
    errors.push({
      // Because plate maps perform a transfer. You can't transfer uncontainable entities.
      errorMessage:
        'Material inputs confirmed via plate map only support entity schemas that are containable',
      type: ProcedureValidationErrorType.UNCONTAINABLE_SCHEMA_ON_MATERIAL_INPUT_CONFIRMED_VIA_PLATE_MAP,
      primaryObject: {
        id: getMethodComponentCid(setting),
        objectType: ProcedureValidationObjectType.MATERIAL_INPUT,
      },
      parentObjects,
    });
  }
  return errors;
}

/**
 * Flags material inputs/outputs constrained to an Equipment fieldset. Equipment is not a valid material
 * type (materials and equipment are distinct procedure concepts), so a fieldset whose entity item type is
 * EQUIPMENT cannot be used here. The fieldset's itemType rides on the link, so no lookup is needed.
 */
function validateNoEquipmentFieldsetMaterialType(
  method: Method,
  parentObjects: Array<ProcedureValidationObject>
): Array<ProcedureValidationError> {
  const materials = [
    ...method.materialInputSettings.map((m) => ({
      cid: getMethodComponentCid(m),
      requiredLink: m.materialInputVersion.dataType.requiredLink,
      objectType: ProcedureValidationObjectType.MATERIAL_INPUT,
    })),
    ...method.materialOutputSettings.map((m) => ({
      cid: getMethodComponentCid(m),
      requiredLink: m.materialOutputVersion.dataType.requiredLink,
      objectType: ProcedureValidationObjectType.MATERIAL_OUTPUT,
    })),
  ];

  return materials
    .filter(
      ({requiredLink}) =>
        isRequiredLinkCustomSchemaInterface(requiredLink) &&
        requiredLink.schemaInterface.itemType === TagSchemaItemType.EQUIPMENT
    )
    .map(({cid, objectType}) => ({
      errorMessage: 'Equipment fieldsets cannot be used as a material type',
      type: ProcedureValidationErrorType.EQUIPMENT_FIELDSET_AS_MATERIAL_TYPE,
      primaryObject: {id: cid, objectType},
      parentObjects,
    }));
}

function getMaterialOutputSettingSchemaLink(
  materialOutputSetting: ProcedureMaterialOutputSetting
): TagSchemaSummary | TagSchemaSummaryGraphQL | null {
  const {requiredLink} = materialOutputSetting.materialOutputVersion.dataType;
  return requiredLink.tagSchema;
}

function isMaterialOutputTypeSelected(materialOutputSetting: ProcedureMaterialOutputSetting): boolean {
  const {requiredLink} = materialOutputSetting.materialOutputVersion.dataType;
  return (
    requiredLink?.tagSchema != null ||
    requiredLink?.folderItemType != null ||
    requiredLink?.schemaInterface != null
  );
}

function doesSchemaHaveInvalidEquipmentInterfaceUsage(
  schema: TagSchemaSummary | TagSchemaSummaryGraphQL,
  shouldBeEquipment: boolean,
  context: ProcedureValidationContext
): boolean {
  if (context.tagSchemasForProcedures == null) {
    // Still loading schemas
    return false;
  }
  const loadedReadableSchema = context.tagSchemasForProcedures.find(({id}) => id === schema.id);
  if (loadedReadableSchema == null) {
    // User does not have read access to schema; leave it to determineProcedureAccessLevel() to lock UI
    return false;
  }
  return shouldBeEquipment !== doesSchemaImplementEquipmentInterface(loadedReadableSchema);
}

function validateMaterialOutputSchemaSelections(
  materialOutputSettings: Array<ProcedureMaterialOutputSetting>,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  const materialOutputsWithSchema = materialOutputSettings.map((m) => ({
    m,
    schema: getMaterialOutputSettingSchemaLink(m),
    hasTypeSelected: isMaterialOutputTypeSelected(m),
  }));

  const missingSchemaErrors = materialOutputsWithSchema
    .filter(({hasTypeSelected}) => !hasTypeSelected)
    .map(({m}) => ({
      errorMessage: 'Material output type is required',
      type: ProcedureValidationErrorType.MISSING_MATERIAL_OUTPUT_TYPE,
      primaryObject: {
        id: getMethodComponentCid(m),
        objectType: ProcedureValidationObjectType.MATERIAL_OUTPUT,
      },
      parentObjects,
    }));

  const materialOutputsWithSchemaLink = materialOutputsWithSchema
    .filter(({schema}) => schema != null)
    .map(({m, schema}) => ({
      m,
      schema: schema!,
    }));

  const archivedSchemaErrors = materialOutputsWithSchemaLink
    .filter(({schema}) => isArchived(schema))
    .map(({m, schema}) => ({
      errorMessage: `${schema.name} schema is archived`,
      type: ProcedureValidationErrorType.ARCHIVED_SCHEMA,
      primaryObject: {
        id: getMethodComponentCid(m),
        objectType: ProcedureValidationObjectType.MATERIAL_OUTPUT,
      },
      parentObjects,
    }));

  const invalidSchemaInterfaceErrors = materialOutputsWithSchemaLink
    .filter(({schema}) => doesSchemaHaveInvalidEquipmentInterfaceUsage(schema, false, context))
    .map(({m, schema}) => ({
      errorMessage: `${schema.name} schema should not have the 'Equipment' fieldset for use as material output type`,
      type: ProcedureValidationErrorType.INVALID_SCHEMA_FOR_PARAMETER_TYPE,
      primaryObject: {
        id: getMethodComponentCid(m),
        objectType: ProcedureValidationObjectType.MATERIAL_OUTPUT,
      },
      parentObjects,
    }));
  return [...missingSchemaErrors, ...archivedSchemaErrors, ...invalidSchemaInterfaceErrors];
}

function validateParameterSettingsSchemaSelection<
  TSetting extends ProcedureMaterialInputSetting | ProcedureEquipmentSetting,
  TType extends TSetting extends ProcedureMaterialInputSetting ? MaterialType : EquipmentType,
>(
  parameterSettings: Array<TSetting>,
  objectType: ProcedureValidationObjectType,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext,
  getDataType: (parameterSetting: TSetting) => TType
): Array<ProcedureValidationError> {
  const parameterSettingWithSchema = parameterSettings
    .filter((ps) => getDataType(ps).requiredLink != null)
    .map((ps) => ({
      ps,
      schema: getRequiredLinkTagSchema({requiredLink: getDataType(ps).requiredLink}),
    }));

  const isEquipmentObjectType = objectType === ProcedureValidationObjectType.EQUIPMENT;
  const objectTypeName = isEquipmentObjectType ? 'Equipment' : 'Material';

  // Show error for existing equipment setting configured as "Any entity" to prompt users to select specific
  // schemas. This error is required for backwards compatibility to prevent users from executing an invalid
  // procedure.
  const nullEntityLinkErrors = isEquipmentObjectType
    ? parameterSettingWithSchema
        .filter(({schema}) => schema == null)
        .map(({ps}) => ({
          errorMessage: `Equipment data type must be a specific equipment schema`,
          type: ProcedureValidationErrorType.INVALID_SCHEMA_FOR_PARAMETER_TYPE,
          primaryObject: {id: getMethodComponentCid(ps), objectType},
          parentObjects,
        }))
    : [];

  const parameterSettingWithSchemaLink = parameterSettingWithSchema
    .filter(({schema}) => schema != null)
    .map(({ps, schema}) => ({
      ps,
      schema: schema!,
    }));

  const archivedSchemaErrors = parameterSettingWithSchemaLink
    .filter(({schema}) => isArchived(schema))
    .map(({ps, schema}) => ({
      errorMessage: `${schema.name} schema is archived`,
      type: ProcedureValidationErrorType.ARCHIVED_SCHEMA,
      primaryObject: {id: getMethodComponentCid(ps), objectType},
      parentObjects,
    }));

  const archivedModelSchemaErrors = isEquipmentObjectType
    ? parameterSettingWithSchemaLink
        .filter(({schema}) => {
          const tagSchema = context.tagSchemas?.find((ts) => ts.id === schema.id);
          return tagSchema != null && hasArchivedModelSchema(tagSchema);
        })
        .map(({ps, schema}) => ({
          errorMessage: `${schema.name} schema's linked model schema is archived`,
          type: ProcedureValidationErrorType.ARCHIVED_SCHEMA,
          primaryObject: {id: getMethodComponentCid(ps), objectType},
          parentObjects,
        }))
    : [];

  const invalidSchemaInterfaceErrors = parameterSettingWithSchemaLink
    .filter(({schema}) =>
      doesSchemaHaveInvalidEquipmentInterfaceUsage(schema, isEquipmentObjectType, context)
    )
    .map(({ps, schema}) => ({
      errorMessage: `${schema.name} schema ${
        isEquipmentObjectType ? 'does' : 'should'
      } not have the 'Equipment' fieldset for use as ${objectTypeName.toLowerCase()} type`,
      type: ProcedureValidationErrorType.INVALID_SCHEMA_FOR_PARAMETER_TYPE,
      primaryObject: {id: getMethodComponentCid(ps), objectType},
      parentObjects,
    }));

  return [
    ...nullEntityLinkErrors,
    ...archivedSchemaErrors,
    ...archivedModelSchemaErrors,
    ...invalidSchemaInterfaceErrors,
  ];
}

function validateEquipmentSchemaSelections(
  equipmentSettings: Array<ProcedureEquipmentSetting>,
  objectType: ProcedureValidationObjectType,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  return validateParameterSettingsSchemaSelection(
    equipmentSettings,
    objectType,
    parentObjects,
    context,
    (e) => e.equipmentVersion.dataType
  );
}

function validateMaterialInputSchemaSelections(
  materialInputSettings: Array<ProcedureMaterialInputSetting>,
  objectType: ProcedureValidationObjectType,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  return validateParameterSettingsSchemaSelection(
    materialInputSettings,
    objectType,
    parentObjects,
    context,
    (m) => m.materialInputVersion.dataType
  );
}

// Flags empty names, names that exceed max length, and duplicate names;
// Flags undefined material output material types;
// Flags invalid Min/Max ranges on float data parameters;
// Flags invalid default planned values for material inputs and equipment;
// Enforces library parameter links for data parameters or lack thereof, based on config flag
function validateParameterSettings(
  method: Method,
  errorMessage: string,
  parentObjects: Array<ProcedureValidationObject>,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  const nonEmptyComponents = {
    dataParameterSettings: method.dataParameterSettings.filter((p) => !isDataParameterSettingEmpty(p)),
    equipmentSettings: method.equipmentSettings.filter(
      (e) => !ProcedureEquipmentTableAdapter.isEquipmentSettingEmpty(e)
    ),
    materialOutputSettings: method.materialOutputSettings.filter(
      (mo) => !ProcedureMaterialOutputsTableAdapter.isMaterialOutputEmpty(mo)
    ),
    materialInputSettings: method.materialInputSettings.filter(
      (mi) => !ProcedureMaterialInputsTableAdapter.isMaterialInputEmpty(mi)
    ),
  };

  const errorsForUnnamedComponents = [
    ...validateNonEmptyMethodSettingNames(
      nonEmptyComponents.dataParameterSettings,
      ProcedureValidationObjectType.DATA_PARAMETER,
      parentObjects
    ),
    ...validateNonEmptyMethodSettingNames(
      nonEmptyComponents.equipmentSettings,
      ProcedureValidationObjectType.EQUIPMENT,
      parentObjects
    ),
    ...validateNonEmptyMethodSettingNames(
      nonEmptyComponents.materialOutputSettings,
      ProcedureValidationObjectType.MATERIAL_OUTPUT,
      parentObjects
    ),
    ...validateNonEmptyMethodSettingNames(
      nonEmptyComponents.materialInputSettings,
      ProcedureValidationObjectType.MATERIAL_INPUT,
      parentObjects
    ),
  ];

  const errorsForNamesExceedingMaxLength = [
    ...validateMethodSettingNamesBelowMaxLength(
      nonEmptyComponents.dataParameterSettings,
      ProcedureValidationObjectType.DATA_PARAMETER,
      'Parameter',
      parentObjects
    ),
    ...validateMethodSettingNamesBelowMaxLength(
      nonEmptyComponents.equipmentSettings,
      ProcedureValidationObjectType.EQUIPMENT,
      'Equipment',
      parentObjects
    ),
    ...validateMethodSettingNamesBelowMaxLength(
      nonEmptyComponents.materialOutputSettings,
      ProcedureValidationObjectType.MATERIAL_OUTPUT,
      'Material',
      parentObjects
    ),
    ...validateMethodSettingNamesBelowMaxLength(
      nonEmptyComponents.materialInputSettings,
      ProcedureValidationObjectType.MATERIAL_INPUT,
      'Material',
      parentObjects
    ),
  ];

  const errorsForInvalidSqlIdentifiers = [
    ...validateParameterNamesCanBecomeValidSqlIdentifiers(
      nonEmptyComponents.dataParameterSettings,
      ProcedureValidationObjectType.DATA_PARAMETER,
      'Parameter',
      parentObjects
    ),
    ...validateParameterNamesCanBecomeValidSqlIdentifiers(
      nonEmptyComponents.equipmentSettings,
      ProcedureValidationObjectType.EQUIPMENT,
      'Equipment',
      parentObjects
    ),
    ...validateParameterNamesCanBecomeValidSqlIdentifiers(
      nonEmptyComponents.materialOutputSettings,
      ProcedureValidationObjectType.MATERIAL_OUTPUT,
      'Material',
      parentObjects
    ),
    ...validateParameterNamesCanBecomeValidSqlIdentifiers(
      nonEmptyComponents.materialInputSettings,
      ProcedureValidationObjectType.MATERIAL_INPUT,
      'Material',
      parentObjects
    ),
  ];

  const componentNames = [
    ...extractMethodComponentsNames(nonEmptyComponents.dataParameterSettings),
    ...extractMethodComponentsNames(nonEmptyComponents.materialInputSettings),
    ...extractMethodComponentsNames(nonEmptyComponents.equipmentSettings),
    ...extractMethodComponentsNames(nonEmptyComponents.materialOutputSettings),
  ];

  const duplicateNames = extractDuplicateNames(componentNames).filter((s) => s.length > 0);

  const duplicateNameErrors = duplicateNames.flatMap((name) => [
    ...createErrorsForMethodComponentsWithMatchingName(
      method.dataParameterSettings,
      ProcedureValidationObjectType.DATA_PARAMETER,
      name,
      ProcedureValidationErrorType.NON_UNIQUE_NAME,
      errorMessage,
      parentObjects
    ),
    ...createErrorsForMethodComponentsWithMatchingName(
      method.materialInputSettings,
      ProcedureValidationObjectType.MATERIAL_INPUT,
      name,
      ProcedureValidationErrorType.NON_UNIQUE_NAME,
      errorMessage,
      parentObjects
    ),
    ...createErrorsForMethodComponentsWithMatchingName(
      method.equipmentSettings,
      ProcedureValidationObjectType.EQUIPMENT,
      name,
      ProcedureValidationErrorType.NON_UNIQUE_NAME,
      errorMessage,
      parentObjects
    ),
    ...createErrorsForMethodComponentsWithMatchingName(
      method.materialOutputSettings,
      ProcedureValidationObjectType.MATERIAL_OUTPUT,
      name,
      ProcedureValidationErrorType.NON_UNIQUE_NAME,
      errorMessage,
      parentObjects
    ),
  ]);

  const valueRangeErrors = [
    ...validateDataParameterMinMaxRanges(nonEmptyComponents.dataParameterSettings, parentObjects),
    ...validateMaterialInputAmounts(nonEmptyComponents.materialInputSettings, parentObjects),
  ];

  const libraryLinkErrors = validateDataParameterLibraryLinkEnforcement(
    nonEmptyComponents.dataParameterSettings,
    parentObjects,
    context.procedureType
  );

  const archivedLibraryDefinitionErrors = validateDataParameterLibraryDefinitionsNotArchived(
    nonEmptyComponents.dataParameterSettings,
    parentObjects,
    context
  );

  const dropdownSelectorErrors = validateDropdownDataParameterSelectors(
    nonEmptyComponents.dataParameterSettings,
    parentObjects
  );

  const otherErrors = [
    ...validateMaterialOutputSchemaSelections(
      nonEmptyComponents.materialOutputSettings,
      parentObjects,
      context
    ),
    ...validateEquipmentSchemaSelections(
      nonEmptyComponents.equipmentSettings,
      ProcedureValidationObjectType.EQUIPMENT,
      parentObjects,
      context
    ),
    ...validateEquipmentSettingsModelsHaveCorrectTypedValues(
      nonEmptyComponents.equipmentSettings,
      parentObjects,
      context
    ),
    ...validateEquipmentModelEntitiesNotArchived(
      nonEmptyComponents.equipmentSettings,
      parentObjects,
      context
    ),
    ...nonEmptyComponents.materialInputSettings.flatMap((setting) =>
      validateMaterialInputConfirmationMethodHasCompatibleDataType(setting, parentObjects, context)
    ),
    ...validateMaterialInputSchemaSelections(
      nonEmptyComponents.materialInputSettings,
      ProcedureValidationObjectType.MATERIAL_INPUT,
      parentObjects,
      context
    ),
    ...validateDefaultMaterialInputSettingsHaveCorrectTypedValues(
      nonEmptyComponents.materialInputSettings,
      parentObjects,
      context
    ),
    ...validateDefaultEquipmentSettingsHaveCorrectTypedValues(
      nonEmptyComponents.equipmentSettings,
      parentObjects,
      context
    ),
    ...validateNoEquipmentFieldsetMaterialType(method, parentObjects),
  ];

  return [
    ...duplicateNameErrors,
    ...errorsForNamesExceedingMaxLength,
    ...errorsForInvalidSqlIdentifiers,
    ...errorsForUnnamedComponents,
    ...valueRangeErrors,
    ...libraryLinkErrors,
    ...archivedLibraryDefinitionErrors,
    ...dropdownSelectorErrors,
    ...otherErrors,
  ];
}

/**
 * Unique constraints from workflows (M0 only):
 * - Parameters, material inputs, and equipment names will all become task schema field names,
 *    therefore all names within these three sections must be unique
 * - Material output names will become output schema field names
 */
export function validateProcedureMethodParameterDefinitions(
  method: Method,
  context: ProcedureValidationContext
): Array<ProcedureValidationError> {
  const parentObjects = [{id: method.cid, objectType: ProcedureValidationObjectType.METHOD}];
  const errorMsg = 'Parameters, materials, and equipment must have unique names within a unit operation';
  return validateParameterSettings(method, errorMsg, parentObjects, context);
}
